import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  clearCouponTransactions,
  fetchCouponTransactionsRequest,
  fetchCouponsRequest,
} from "../../../redux/CustomerCoupon/customerCouponSlice";
import TableCompo from "../Table/TableCompo";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `₹ ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "success") return { bg: "#e8f8ef", color: "#1b7a45" };
  if (value === "failed" || value === "failure") {
    return { bg: "#fdecec", color: "#b42318" };
  }
  if (value === "pending") return { bg: "#fff7e8", color: "#b54708" };
  return { bg: "#f3eaff", color: "#7028DA" };
};

export default function IssuedCouponsTab() {
  const dispatch = useDispatch();
  const {
    coupons,
    couponTotal,
    couponsLoading,
    couponTransactions,
    couponTransactionTotal,
    couponTransactionsLoading,
    couponTransactionMeta,
  } = useSelector((state) => state.customerCoupon);
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [txnPopup, setTxnPopup] = useState({ open: false, row: null });
  const [txnPage, setTxnPage] = useState(1);
  const txnLimit = 5;

  useEffect(() => {
    dispatch(
      fetchCouponsRequest({
        start: pagination.page * pagination.limit,
        limit: pagination.limit,
        search,
      }),
    );
  }, [dispatch, pagination, search]);

  useEffect(() => {
    if (!txnPopup.open || !txnPopup.row?._id) return;
    dispatch(
      fetchCouponTransactionsRequest({
        couponId: txnPopup.row._id,
        start: (txnPage - 1) * txnLimit,
        limit: txnLimit,
      }),
    );
  }, [dispatch, txnPopup.open, txnPopup.row?._id, txnPage]);

  const openTxnPopup = (row) => {
    setTxnPage(1);
    setTxnPopup({ open: true, row });
  };

  const closeTxnPopup = () => {
    setTxnPopup({ open: false, row: null });
    dispatch(clearCouponTransactions());
  };

  const rows = (coupons || []).map((item) => ({
    ...item,
    id: item._id,
    validTill: item.validTill
      ? new Date(item.validTill).toLocaleDateString("en-IN")
      : "-",
  }));

  const totalTxnPages = Math.max(
    1,
    Math.ceil((couponTransactionTotal || 0) / txnLimit),
  );
  const totalPoints =
    couponTransactionMeta?.couponPoints ??
    Number(txnPopup.row?.couponPoints || 0);

  const columns = [
    { field: "jewellerName", headerName: "Jeweller Name" },
    { field: "couponCode", headerName: "Coupon code" },
    { field: "customerName", headerName: "Customer" },
    { field: "phone", headerName: "Phone" },
    {
      field: "couponPoints",
      headerName: "Points",
      minWidth: 90,
      render: (value) => Number(value || 0),
    },
    { field: "orderType", headerName: "Last txn" },
    { field: "amount", headerName: "Last amount" },
    { field: "rewardTitle", headerName: "Reward" },
    { field: "couponType", headerName: "Coupon type" },
    { field: "validTill", headerName: "Valid till" },
    { field: "status", headerName: "Status" },
    {
      field: "detailsAction",
      headerName: "Details",
      hideInExcel: true,
      sortable: false,
      minWidth: 120,
      render: (_val, row) => (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            openTxnPopup(row);
          }}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            px: 1.8,
            fontWeight: 600,
            boxShadow: "none",
            background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
            "&:hover": {
              boxShadow: "none",
              background: "linear-gradient(90deg, #5f1fc0 0%, #8a2fc4 100%)",
            },
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <TableCompo
        columns={columns}
        data={rows}
        loading={couponsLoading}
        page={pagination.page}
        limit={pagination.limit}
        totalCount={couponTotal}
        onTableChange={(action, tableState) => {
          if (action === "changePage") {
            setPagination((prev) => ({ ...prev, page: tableState.page }));
          }
          if (action === "changeRowsPerPage") {
            setPagination({ page: 0, limit: tableState.rowsPerPage });
          }
          if (action === "search") {
            setSearch(tableState.searchText || "");
            setPagination((prev) => ({ ...prev, page: 0 }));
          }
        }}
      />

      <Dialog
        open={txnPopup.open}
        onClose={closeTxnPopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(72, 24, 140, 0.22)",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: "linear-gradient(120deg, #7028DA 0%, #9E3ED7 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.4 }}>
              Transaction history
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
              {txnPopup.row?.rewardTitle || "Lucky draw coupon"}
            </Typography>
          </Box>
          <IconButton
            onClick={closeTxnPopup}
            size="small"
            sx={{ color: "#fff", mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, backgroundColor: "#faf8ff" }}>
          <Box sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: "14px",
                backgroundColor: "#fff",
                border: "1px solid #ebe4f8",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#8a7aa8",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.5,
                  }}
                >
                  Coupon code
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 22,
                    letterSpacing: 1.5,
                    color: "#2d2148",
                  }}
                >
                  {txnPopup.row?.couponCode || "-"}
                </Typography>
                <Typography sx={{ mt: 0.8, fontSize: 13, color: "#5f5378" }}>
                  {txnPopup.row?.customerName || "-"}
                  {txnPopup.row?.phone ? ` · ${txnPopup.row.phone}` : ""}
                </Typography>
              </Box>
              <Chip
                label={`Total points: ${Number(totalPoints || 0)}`}
                sx={{
                  fontWeight: 700,
                  backgroundColor: "#f3eaff",
                  color: "#7028DA",
                }}
              />
            </Stack>

            {couponTransactionsLoading &&
            (!couponTransactions || couponTransactions.length === 0) ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <CircularProgress size={32} sx={{ color: "#7028DA" }} />
              </Box>
            ) : couponTransactions && couponTransactions.length > 0 ? (
              <>
                <TableContainer
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #ebe4f8",
                    backgroundColor: "#fff",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f6f1ff" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {couponTransactions.map((txn) => {
                        const style = statusColor(txn.status);
                        return (
                          <TableRow key={txn._id} hover>
                            <TableCell>
                              {formatDateTime(txn.createdAt)}
                            </TableCell>
                            <TableCell>{formatValue(txn.orderId)}</TableCell>
                            <TableCell>
                              {formatValue(txn.orderType)}
                              {txn.metalName && txn.metalName !== "-"
                                ? ` · ${txn.metalName}`
                                : ""}
                            </TableCell>
                            <TableCell>
                              {formatAmount(txn.final_amount)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#7028DA" }}>
                              +{Number(txn.couponPoints || 0)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={formatValue(txn.status)}
                                sx={{
                                  fontWeight: 700,
                                  textTransform: "capitalize",
                                  backgroundColor: style.bg,
                                  color: style.color,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {couponTransactionTotal > txnLimit && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#5f5378" }}>
                      {couponTransactionTotal} transaction
                      {couponTransactionTotal === 1 ? "" : "s"}
                    </Typography>
                    <Pagination
                      count={totalTxnPages}
                      page={txnPage}
                      onChange={(_e, page) => setTxnPage(page)}
                      color="secondary"
                      size="small"
                    />
                  </Stack>
                )}
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#5f5378" }}>
                  No transactions linked to this coupon yet.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: "#fff",
            borderTop: "1px solid #ebe4f8",
          }}
        >
          <Button
            variant="contained"
            onClick={closeTxnPopup}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              fontWeight: 700,
              boxShadow: "none",
              background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 100%)",
              "&:hover": {
                boxShadow: "none",
                background: "linear-gradient(90deg, #5f1fc0 0%, #8a2fc4 100%)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
